<template>
  <div
    class="fixed inset-0 z-mobile-search-menu bg-black-000_a5 desktop:top-header-height desktop:z-menu"
    @click.self="handleClose"
    @keydown.esc="handleClose"
  >
    <div class="max-h-full overflow-y-auto overscroll-contain bg-white">
      <!-- Search Bar -->
      <div class="container-wrap py-2 tablet:py-6">
        <div role="search" class="flex items-center gap-3 tablet:gap-6">
          <div
            class="flex h-12 min-w-0 flex-1 items-center gap-3 rounded-full bg-white px-4 shadow-button shadow-brand-400 desktop:h-14 desktop:px-5"
          >
            <IconSearch class="h-6 w-6 shrink-0" />
            <input
              ref="inputRef"
              v-model.trim="inputValue"
              type="search"
              autocomplete="off"
              maxlength="100"
              :placeholder="t('search-menu.placeholder')"
              class="h-full min-w-0 flex-1 text-sm placeholder-gray-600 tablet:text-base"
              @keyup.enter="handleEnter"
            />
            <button
              v-show="inputValue"
              type="button"
              title="clear"
              class="flex h-6 w-6 shrink-0 items-center justify-center"
              @click="handleClear"
            >
              <IconClose class="h-full w-full" />
            </button>
          </div>
          <button
            type="button"
            title="close"
            class="flex h-6 w-6 shrink-0 items-center justify-center"
            @click="handleClose"
          >
            <IconClose class="h-full w-full" />
          </button>
        </div>
      </div>

      <!-- Search Results -->
      <div v-if="inputValue" class="container-wrap py-8 tablet:py-12">
        <LoaderBounce v-if="isLoading" class="py-16" />
        <SearchEmpty
          v-else-if="searchResults.length === 0"
          class="rounded-2xl border border-solid border-gray-500 tablet:rounded-3xl"
          @clear="handleClear"
        />
        <div v-else class="desktop:flex">
          <div
            v-if="hasMain"
            class="flex min-w-0 flex-col gap-11 desktop:flex-1 desktop:pr-8"
          >
            <LayoutSearchSection
              v-if="groups.products.length"
              :title="t('search-menu.type.products')"
            >
              <ul
                class="grid grid-cols-1 gap-3 tablet:auto-rows-fr tablet:grid-cols-2 desktop:grid-cols-3 desktop:gap-5"
              >
                <li v-for="item in groups.products" :key="item.pageUrl">
                  <LayoutSearchProductCard :item="item" :keyword="keyword" />
                </li>
                <li>
                  <TheNuxtLink
                    :to="viewAllProductsUrl"
                    :title="t('search-menu.view-all-products')"
                    class="flex h-full items-center justify-center rounded-full bg-black py-3 text-center text-sm font-bold text-white hover:bg-gray-800 active:bg-gray-700 tablet:flex-col tablet:items-start tablet:justify-end tablet:rounded-2xl tablet:p-5 tablet:text-left tablet:text-lg"
                    @click="handleClose"
                  >
                    {{ t('search-menu.view-all-products') }}
                    <IconArrow
                      direction="right"
                      class="mt-5 hidden h-6 w-6 tablet:block"
                    />
                  </TheNuxtLink>
                </li>
              </ul>
            </LayoutSearchSection>
            <LayoutSearchSection
              v-if="groups.results.length"
              :title="t('search-menu.search-results')"
            >
              <ul class="flex flex-col gap-6 tablet:gap-10">
                <li v-for="item in groups.results" :key="item.pageUrl">
                  <LayoutSearchItem :item="item" :keyword="keyword" />
                </li>
              </ul>
            </LayoutSearchSection>
            <TheButton
              v-if="!hasAside"
              :to="showMoreUrl"
              type="tonal"
              :title="t('search-menu.show-more-results')"
              class="w-full font-bold desktop:w-72"
              @click="handleClose"
            >
              {{ t('search-menu.show-more-results') }}
            </TheButton>
          </div>

          <aside
            v-if="hasAside"
            class="flex min-w-0 flex-col gap-11"
            :class="
              hasMain
                ? 'mt-11 desktop:mt-0 desktop:w-[22rem] desktop:shrink-0 desktop:border-l desktop:border-solid desktop:border-gray-500 desktop:pl-8'
                : 'flex-1'
            "
          >
            <LayoutSearchSection
              v-if="groups.industryFocuses.length"
              :title="t('search-menu.type.industryFocuses')"
            >
              <ul class="flex flex-col gap-6 tablet:gap-8">
                <li v-for="item in groups.industryFocuses" :key="item.pageUrl">
                  <LayoutSearchItem
                    :item="item"
                    :keyword="keyword"
                    :has-description="false"
                  />
                </li>
              </ul>
            </LayoutSearchSection>
            <LayoutSearchSection
              v-if="groups.caseStudies.length"
              :title="t('search-menu.type.caseStudies')"
            >
              <ul class="flex flex-col gap-6 tablet:gap-8">
                <li v-for="item in groups.caseStudies" :key="item.pageUrl">
                  <LayoutSearchItem
                    :item="item"
                    :keyword="keyword"
                    :has-description="false"
                  />
                </li>
              </ul>
            </LayoutSearchSection>
            <TheButton
              :to="showMoreUrl"
              type="tonal"
              :title="t('search-menu.show-more-results')"
              class="w-full font-bold"
              :class="!hasMain && 'desktop:w-72'"
              @click="handleClose"
            >
              {{ t('search-menu.show-more-results') }}
            </TheButton>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { refDebounced } from '@vueuse/core';

  const router = useRouter();
  const { t, locale } = useI18n();
  const { isMobile } = useScreen();
  const searchMenuStore = useSearchMenuStore();
  const { searchValue, searchResults } = storeToRefs(searchMenuStore);

  const inputValue = ref<string>(searchValue.value);
  const debounced = refDebounced(inputValue, 500);
  const inputRef = ref<HTMLInputElement | null>(null);

  const { status, execute } = await searchMenuStore.USE_GET_SEARCH();

  const keyword = computed((): string => searchValue.value.trim());

  const isLoading = computed(
    (): boolean =>
      keyword.value !== inputValue.value || status.value === 'pending'
  );

  const groups = computed(() =>
    provideSearchGroups(searchResults.value, isMobile.value)
  );

  const hasMain = computed(
    (): boolean =>
      !!(groups.value.products.length || groups.value.results.length)
  );
  const hasAside = computed(
    (): boolean =>
      !!(groups.value.industryFocuses.length || groups.value.caseStudies.length)
  );

  const viewAllProductsUrl = computed(
    (): string => `/${locale.value}/marketplace/category/featured-products`
  );
  const showMoreUrl = computed(
    (): string =>
      `/${locale.value}/marketplace/search?keyword=${encodeURIComponent(keyword.value)}&page=1`
  );

  const handleClose = (): void => {
    searchMenuStore.HANDLE_CLOSE_MENU();
  };

  const handleClear = (): void => {
    inputValue.value = '';
    searchValue.value = '';
    searchResults.value = [];
    inputRef.value?.focus();
  };

  const handleEnter = (): void => {
    if (!inputValue.value) {
      return;
    }
    router.push({
      path: `/${locale.value}/marketplace/search`,
      query: { keyword: inputValue.value, page: 1 },
    });
    handleClose();
  };

  watch(debounced, (value: string) => {
    searchValue.value = value;
  });

  watch(searchValue, (value: string) => {
    if (value.trim()) {
      execute();
    }
  });

  onMounted(() => {
    nextTick(() => {
      inputRef.value?.focus();
    });
  });
</script>
